import { z } from 'zod';
import type { Permission } from '../permissions';

// RFC 9728 protected-resource metadata for the workspace MCP endpoint
// (IMM-601.8.1). The document is how a hosted MCP client learns WHERE to get a
// token for this resource, so it must be reachable without a credential — the
// leaf carries `permission: 'PUBLIC'` and no `docs` marker (it is a protocol
// document, not a partner-facing HTTP operation).

/**
 * The scopes the MCP resource needs: every permission a tool in the workspace
 * MCP catalogue requires, minus the transport permission.
 *
 * DERIVED, NOT CURATED (IMM-698 / BL-083). It is written out here because this
 * is a wire contract whose order is public, but it is not maintained by hand:
 * `mcp-scopes-supported-derivation.test.ts` in the backend's app suite builds the
 * real catalogue from the same module builders `server.ts` spreads and asserts
 * set-equality in both directions, so adding a tool fails that test until this
 * list follows. The previous version advertised three read scopes described as
 * „the minimal read set the six base tools need" — while the catalogue had held
 * eighteen tools since nine hours before that sentence was written, which left
 * every write unreachable over OAuth.
 *
 * `workspace:identity:mcp-invoke` is deliberately EXCLUDED: it is the transport
 * permission `workspace_list` is gated on, injected by the credential resolver,
 * held by no role and absent from `API_KEY_GRANTABLE_PERMISSIONS` — advertising
 * it would send a client into a consent flow it can never complete.
 *
 * `offline_access` is deliberately NEVER listed: refresh is a client concern,
 * not a resource requirement.
 *
 * Scopes ARE permission literals verbatim — one vocabulary, so a `403`
 * `insufficient_scope` challenge names the exact grant a member must add. The
 * drift test in the backend's identity unit suite pins this set as a subset of
 * `API_KEY_GRANTABLE_PERMISSIONS`, so a scope no member can ever be granted
 * cannot be advertised here.
 *
 * NOTE the deliberate asymmetry with the AUTHORIZATION SERVER document, which
 * advertises the full `API_KEY_GRANTABLE_PERMISSIONS`: an AS advertises what may
 * be REQUESTED, this document advertises what the RESOURCE needs. They are not
 * meant to be equal.
 */
export const MCP_SCOPES_SUPPORTED = [
  // Catalogue ORDER, not a tidy grouping: the derivation test compares this
  // list against the composed catalogue element by element, because the
  // document is served verbatim and its order is public.
  //
  // IMM-873.1 — the identity directory reads. FIRST because this list is
  // pinned in CATALOGUE order (the document is served verbatim), and
  // `server.ts` composes `buildIdentityMcpTools` before every other builder;
  // `workspace_list`'s own transport grant is the excluded one above it.
  'workspace:identity:member-read',
  'workspace:identity:organizations-read',
  // IMM-270.4 — the Vorlagen reads, registered by `buildIdentityMcpTools` right
  // after `organization_list`, so catalogue order puts the grant here.
  'workspace:identity:document-template-read',
  'workspace:identity:email-template-read',
  'workspace:identity:email-template-create',
  'workspace:identity:email-template-update',
  // The assistant-shared reads.
  'workspace:property:property-read',
  'workspace:lead:lead-read',
  'workspace:deal:deal-read',
  // IMM-769.2 — contacts, the first relationship read a client can reach.
  'workspace:relationship:contact-read',
  // The wave-2 reads and the activity writes (IMM-601.3 / IMM-601.4).
  'organization:activity:activity-read',
  'organization:activity:activity-write',
  'organization:activity:activity-update',
  'organization:property:document-read',
  'organization:property:media-read',
  // IMM-769.3 — the property set, catalogued beside its module siblings.
  'organization:property:property-read',
  'organization:property:property-create',
  'organization:property:property-contact-write',
  'organization:property:property-reassign',
  // IMM-769.6 — the outbound document request.
  'organization:property:document-request',
  // IMM-893.1 — filing a document, immediately after: the property module's
  // builder registers `property.fileDocument` right after
  // `property.createDocumentRequest`.
  'organization:property:document-write',
  // IMM-895.4 — the four property write tools, registered immediately after
  // `property.linkDocument` and so before valuation's builder composes.
  // `organization:property:property-contact-write` and
  // `organization:property:document-write` are already above; this is the
  // one new de-duplicated scope.
  'organization:property:property-update',
  // IMM-1165.3 / IMM-1164.3 — adding a video by link and a 360° tour, registered after property.fileExpose.
  'organization:property:media-write',
  // IMM-1161.2 — the web exposé writes, registered last in the property block.
  'organization:property:portal-listing-publish',
  'organization:property:portal-listing-withdraw',
  'organization:valuation:valuation-request-read',
  // IMM-895.6 — the requesting half of the valuation lifecycle, registered
  // immediately after the reads within the valuation module's own builder.
  // INSERTED here, not appended: this list is compared in catalogue order.
  'organization:valuation:valuation-request-create',
  // The write set's close (IMM-601.5).
  'organization:lead:lead-create',
  // IMM-769.5 — the lead set.
  'organization:lead:lead-update',
  'organization:lead:lead-assign',
  'organization:deal:deal-phase-advance',
  'organization:deal:deal-phase-move-back',
  // IMM-769.4 — the deal set.
  'organization:deal:deal-create',
  'organization:deal:deal-assign',
  'organization:deal:deal-update',
  // IMM-268.3 — the deal's documents, registered right after the deal set: the first tool to need the Standort-tier read.
  'organization:deal:deal-read',
  // IMM-1064.8 — the Verkaufs-Pipeline, in tool-registration order.
  'workspace:deal:prospect-read',
  'organization:deal:prospect-create',
  'organization:deal:prospect-phase-advance',
  'organization:deal:prospect-phase-move-back',
  'organization:deal:prospect-update',
  // IMM-769.2 — the company set.
  'workspace:relationship:company-read',
  'organization:relationship:company-read',
  // IMM-895.2 — the contact write set, in tool-registration order.
  // `contact_note` and `contact_communication` reuse `contact-update`, which
  // the derivation de-duplicates, so they contribute nothing new here.
  'organization:relationship:contact-create',
  'organization:relationship:contact-update',
  'organization:relationship:contact-note-update',
  'organization:relationship:contact-communication-update',
  // IMM-895.3 — the company write set, in tool-registration order. The two
  // `company-contact-write` tools contribute one de-duplicated scope entry.
  'organization:relationship:company-create',
  'organization:relationship:company-update',
  'organization:relationship:company-archive',
  'organization:relationship:company-unarchive',
  'organization:relationship:company-contact-write',
] as const satisfies ReadonlyArray<Permission>;

export type McpSupportedScope = (typeof MCP_SCOPES_SUPPORTED)[number];

/**
 * The RFC 9728 document body. `resource` is the canonical MCP URI — no trailing
 * slash, no fragment — and is what an access token's audience must equal;
 * `authorization_servers` names the issuer(s) a client may obtain that token
 * from — since IMM-601.8.3 that is UNCONDITIONALLY immoOS itself, because we are
 * our own authorization server and an empty array would publish an honest but
 * unusable document; `bearer_methods_supported` is `['header']` because the
 * endpoint reads the `Authorization` header and nothing else.
 */
export const ProtectedResourceMetadataSchema = z.object({
  resource: z.url(),
  authorization_servers: z.array(z.url()),
  scopes_supported: z.array(z.string()),
  bearer_methods_supported: z.array(z.literal('header')),
});
export type ProtectedResourceMetadata = z.infer<typeof ProtectedResourceMetadataSchema>;
